package com.solevo.dist.pages

import androidx.compose.runtime.*
import com.solevo.dist.components.DashboardPageLayout
import com.solevo.dist.components.RequireAuth
import com.solevo.dist.components.WaitForAuthAndViewModel
import com.solevo.dist.viewmodels.SettingsTab
import com.solevo.dist.viewmodels.SettingsUiState
import com.solevo.dist.viewmodels.SettingsViewModel
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.solevo.dist.navigation.Screen
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.components.graphics.Image
import com.solevo.dist.util.Res
import com.solevo.dist.i18n.LocalStrings
import com.solevo.dist.theme.styles.sectionTitle
import org.jetbrains.compose.web.css.*

@Page("/settings")
@Composable
fun SettingsScreen() {
    RequireAuth { SettingsPage() }
}

@Composable
private fun VSpace(h: CSSLengthValue) = Box(Modifier.height(h))

@Composable
private fun SettingsPage() {
    WaitForAuthAndViewModel(
        key = "settings",
        viewModelFactory = { SettingsViewModel() },
        onViewModelReady = { vm -> vm.start() }
    ) { vm ->
        DisposableEffect(Unit) { onDispose { vm.stop() } }
        val ui by vm.state.collectAsState()
        val context = rememberPageContext()
        DashboardPageLayout {
            SettingsContent(
                ui = ui,
                onTabSelected = { vm.selectTab(it) },
                onOpenLanguage = { vm.openLanguageCard() },
                onOpenTermsAndPrivacy = { context.router.navigateTo(Screen.TermsAndPrivacy.route) },
                onDismissLanguage = { vm.dismissLanguageCard() },
                onSelectLanguage = { code -> vm.setSelectedLanguage(code) },
                onApplyLanguage = { vm.applySelectedLanguage() },
                onLogout = { vm.logout() }
            )
        }
    }
}

@Composable
private fun SettingsContent(
    ui: SettingsUiState,
    onTabSelected: (SettingsTab) -> Unit,
    onOpenLanguage: () -> Unit,
    onOpenTermsAndPrivacy: () -> Unit,
    onDismissLanguage: () -> Unit,
    onSelectLanguage: (String) -> Unit,
    onApplyLanguage: () -> Unit,
    onLogout: () -> Unit
) {
    Column(Modifier.fillMaxWidth().padding(24.px)) {
        val strings = LocalStrings.current
        // Header row with title and logout button
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            SpanText(strings.settingsTitle, Modifier.sectionTitle())
            Box(Modifier.flexGrow(1.0))
            Row(
                Modifier.cursor(Cursor.Pointer).gap(8.px)
                    .padding(8.px)
                    .borderRadius(10.px)
                    .onClick { onLogout() }
                    .styleModifier { property("user-select", "none") }
            ) {
                Box(
                    Modifier
                        .width(18.px)
                        .height(18.px)
                        .flexShrink(0)
                ) {
                    Image(src = Res.Icon.SettingsIcons.logout, alt = "logout", modifier = Modifier.styleModifier {
                        property("filter", "brightness(0) saturate(100%) invert(42%) sepia(94%) saturate(2845%) hue-rotate(210deg) brightness(101%) contrast(93%)")
                    })
                }
                SpanText(strings.settingsLogout, Modifier.color(rgb(43, 111, 243)))
            }
        }
        VSpace(16.px)

        // Tabs
        Row(Modifier.gap(16.px)) {
            TopTab(strings.settingsTabPersonal, ui.selectedTab == SettingsTab.Personal) { onTabSelected(SettingsTab.Personal) }
            TopTab(strings.settingsTabCompany, ui.selectedTab == SettingsTab.Company) { onTabSelected(SettingsTab.Company) }
        }
        VSpace(16.px)

        // Body: left list, optional right language card
        Row(
            Modifier.fillMaxWidth().gap(24.px).styleModifier { property("align-items", "flex-start") }
        ) {
            Column(
                Modifier.width(360.px).gap(0.px)
            ) {
                val items = if (ui.selectedTab == SettingsTab.Personal) ui.personal else ui.company
                items.forEachIndexed { index, item ->
                    val displayTitle = localizeSettingsLabel(LocalStrings.current, item.title)
                    SettingsListItem(
                        title = displayTitle,
                        value = item.value,
                        valueColor = item.valueColorHex,
                        showChevron = item.isLanguage || item.isTermsAndPrivacy,
                        onClick = {
                            when {
                                item.isLanguage -> onOpenLanguage()
                                item.isTermsAndPrivacy -> onOpenTermsAndPrivacy()
                            }
                        },
                        iconSrc = settingsIconFor(item.title, ui.selectedTab == SettingsTab.Company),
                        showDivider = index != items.lastIndex
                    )
                }
            }

            if (ui.showLanguageCard) {
                LanguageCard(
                    languages = ui.languages,
                    selectedLanguage = ui.selectedLanguageCode,
                    onSelect = onSelectLanguage,
                    onApply = onApplyLanguage,
                    onDismiss = onDismissLanguage
                )
            }
        }
    }
}

@Composable
private fun TopTab(label: String, selected: Boolean, onClick: () -> Unit) {
    Box(
        Modifier
            .cursor(Cursor.Pointer)
            .padding(leftRight = 12.px, topBottom = 10.px)
            .styleModifier { if (selected) property("box-shadow", "inset 0 -3px 0 0 black") }
            .onClick { onClick() }
    ) {
        SpanText(
            label,
            Modifier.fontSize(15.px)
                .fontWeight(if (selected) FontWeight.Bold else FontWeight.Normal)
                .color(rgb(36, 41, 47))
        )
    }
}

@Composable
private fun IconBox() {
    Box(
        Modifier.size(18.px).borderRadius(4.px).backgroundColor(rgb(230, 232, 235))
    )
}


@Composable
private fun SettingsListItem(
    title: String,
    value: String,
    valueColor: String?,
    showChevron: Boolean,
    onClick: () -> Unit,
    iconSrc: String = Res.Icon.OrderIcons.accountBalance,
    showDivider: Boolean
) {
    Column(Modifier.fillMaxWidth()) {
        Row(
            Modifier.fillMaxWidth()
                .padding(leftRight = 0.px, topBottom = 14.px)
                .gap(12.px)
                .alignItems(AlignItems.Center)
                .cursor(if (showChevron) Cursor.Pointer else Cursor.Default)
                .onClick { if (showChevron) onClick() }
        ) {
            Box(
                Modifier
                    .width(18.px)
                    .height(18.px)
                    .flexShrink(0)
                    .margin(right = 10.px)
            ) { Image(src = iconSrc, alt = "icon") }
            Column(Modifier.flexGrow(1.0)) {
                SpanText(title, Modifier.fontSize(13.px).color(rgb(120, 130, 140)))
                val color = valueColor?.let { rgbFromHex(it) } ?: rgb(36, 41, 47)
                SpanText(value, Modifier.fontSize(14.px).color(color))
            }
            if (showChevron) {
                Box(
                    Modifier.width(8.px).height(14.px)
                        .styleModifier { property("mask", "linear-gradient(90deg, black, black)") }
                )
            }
        }
        if (showDivider) {
            Box(Modifier.fillMaxWidth().height(1.px).backgroundColor(rgb(240, 242, 246)))
        }
    }
}

@Composable
private fun LanguageCard(
    languages: List<Pair<String, String>>, // label to code
    selectedLanguage: String,
    onSelect: (String) -> Unit,
    onApply: () -> Unit,
    onDismiss: () -> Unit
) {
    Column(
        Modifier.width(600.px)
            .backgroundColor(rgb(255, 255, 255))
            .border(1.px, LineStyle.Solid, rgb(232, 235, 240))
            .borderRadius(12.px)
            .padding(20.px)
            .gap(12.px)
    ) {
        val strings = LocalStrings.current
        SpanText(strings.languageTitle, Modifier.fontSize(16.px).fontWeight(FontWeight.Medium))
        // Ensure a selected indicator even on first open (fallback to current localization language)
        val currentLang by com.solevo.dist.i18n.Localization.language.collectAsState()
        val effectiveSelected = if (selectedLanguage.isBlank()) currentLang else selectedLanguage

        languages.forEach { (label, code) ->
            Row(
                Modifier.gap(8.px).alignItems(AlignItems.Center)
                    .cursor(Cursor.Pointer)
                    .onClick { onSelect(code) }
            ) {
                // simple radio look
                val selected = code == effectiveSelected
                Box(
                    Modifier.size(14.px)
                        .border(1.px, LineStyle.Solid, if (selected) rgb(30, 100, 255) else rgb(210, 214, 219))
                        .borderRadius(999.px)
                        .backgroundColor(if (selected) rgb(30, 100, 255) else rgb(255, 255, 255))
                )
                SpanText(label, Modifier.fontSize(14.px).color(rgb(36, 41, 47)))
            }
        }
        VSpace(12.px)
        Row(Modifier.gap(12.px)) {
            Box(
                Modifier.cursor(Cursor.Pointer)
                    .backgroundColor(rgb(30, 100, 255))
                    .color(rgb(255, 255, 255))
                    .padding(leftRight = 16.px, topBottom = 10.px)
                    .borderRadius(8.px)
                    .onClick { onApply() }
            ) { SpanText(strings.apply) }
            Box(
                Modifier.cursor(Cursor.Pointer)
                    .padding(leftRight = 16.px, topBottom = 10.px)
                    .borderRadius(8.px)
                    .border(1.px, LineStyle.Solid, rgb(232, 235, 240))
                    .onClick { onDismiss() }
            ) { SpanText(strings.close) }
        }
    }
}

private fun rgbFromHex(hex: String): CSSColorValue {
    // Expecting formats like #RRGGBB
    val clean = hex.removePrefix("#")
    val r = clean.substring(0, 2).toInt(16)
    val g = clean.substring(2, 4).toInt(16)
    val b = clean.substring(4, 6).toInt(16)
    return rgb(r, g, b)
}

private fun settingsIconFor(title: String, isCompanySection: Boolean): String {
    // Special-case: in Company tab, the "Name" row refers to company name.
    if (isCompanySection && title.equals("name", ignoreCase = true)) {
        return Res.Icon.SettingsIcons.company
    }
    return when (title.lowercase()) {
        "name" -> Res.Icon.SettingsIcons.person
        "customer id" -> Res.Icon.SettingsIcons.customerId
        "country" -> Res.Icon.SettingsIcons.location
        "language" -> Res.Icon.SettingsIcons.language
        "phone number" -> Res.Icon.SettingsIcons.call
        "id" -> Res.Icon.SettingsIcons.customerId
        "address" -> Res.Icon.SettingsIcons.location
        "tier" -> Res.Icon.SettingsIcons.diamond
        "company" -> Res.Icon.SettingsIcons.company
        "terms & privacy" -> Res.Icon.SettingsIcons.termsAndPrivacy
        else -> Res.Icon.OrderIcons.accountBalance
    }
}

private fun localizeSettingsLabel(s: com.solevo.dist.i18n.Strings, title: String): String {
    return when (title.lowercase()) {
        "name" -> s.labelName
        "customer id" -> s.labelCustomerId
        "country" -> s.labelCountry
        "language" -> s.labelLanguage
        "phone number" -> s.labelPhoneNumber
        "id" -> s.labelId
        "address" -> s.labelAddress
        "tier" -> s.labelTier
        "company" -> s.labelCompany
        "terms & privacy" -> s.labelTermsAndPrivacy
        else -> title
    }
}